import React from 'react';
import RecoverableImage from '../../features/images/RecoverableImage';
import { useWebPreview, PlaybackRing } from '../../features/preview/PreviewControls';
import {
  webPreviewPlayer,
  previewKey,
  previewTrack,
  canPreview,
} from '../../features/preview/previewPlayer';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiMenu, FiX, FiPlus, FiCheck, FiPlay, FiPause } from 'react-icons/fi';
import PlaylistItemTitle from './PlaylistItemTitle';
import { editorItemKey, movedEditorItems } from './playlistEditorModel';
function Artwork({ item }) {
  return (
    <span className="pp-editor-item-cover">
      {(item.albumImageUrl || item.imageUrl) && (
        <RecoverableImage src={item.albumImageUrl || item.imageUrl} alt="" />
      )}
      <span aria-hidden="true">P</span>
    </span>
  );
}
function Copy({ item, type, search = false, relation, en }) {
  return (
    <div className={`pp-editor-item-copy ${type === 'TRACK' ? 'is-track' : ''}`}>
      <PlaylistItemTitle title={item.title} explicit={item.contentRating === 'EXPLICIT'} />
      <span>
        <span>{item.artist?.name || item.artistName || '—'}</span>
        {search && item.releaseDate && <small> · {item.releaseDate}</small>}
      </span>
      {relation && (
        <small className="pp-editor-relation">
          {relation === 'SAME_ARTIST'
            ? en
              ? 'Same Artist'
              : '같은 아티스트'
            : en
              ? 'Recommended Artist'
              : '추천 아티스트'}
        </small>
      )}
    </div>
  );
}
function Selected({ item, type, index, count, busy, en, onMove, onRemove }) {
  const key = editorItemKey(item),
    { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: key,
      disabled: busy,
    });
  return (
    <li
      ref={setNodeRef}
      className="pp-editor-selected-row"
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        disabled={busy}
        className="pp-editor-drag"
        aria-label={`${item.title} ${en ? 'reorder' : '순서 변경'}`}
      >
        <FiMenu size={18} />
      </button>
      <Artwork item={item} />
      <Copy item={item} type={type} />
      <span className="pp-editor-move pp-editor-sr-only">
        <button
          type="button"
          disabled={busy || index === 0}
          aria-label={`${item.title} ${en ? 'move up' : '위로 이동'}`}
          onClick={() => onMove(index, index - 1)}
        >
          ↑
        </button>
        <button
          type="button"
          disabled={busy || index === count - 1}
          aria-label={`${item.title} ${en ? 'move down' : '아래로 이동'}`}
          onClick={() => onMove(index, index + 1)}
        >
          ↓
        </button>
      </span>
      <button
        type="button"
        disabled={busy}
        aria-label={`${item.title} ${en ? 'remove' : '제거'}`}
        onClick={() => onRemove(key)}
      >
        <FiX size={18} />
      </button>
    </li>
  );
}
function ResultRow({ item, type, relation, selected, busy, en, onAdd, owner }) {
  const playback = useWebPreview(),
    track = previewTrack({ ...item, artistName: item.artist?.name || item.artistName }),
    isTrack = type === 'TRACK',
    active = isTrack && playback.track && previewKey(playback.track) === previewKey(track),
    resolving = active && playback.status === 'loading',
    playing = active && playback.status === 'playing';
  const play = () => {
    if (busy || resolving) return;
    if (active) webPreviewPlayer.toggle(owner);
    else void webPreviewPlayer.play(track, [track], owner);
  };
  return (
    <li
      className={`pp-editor-result-row ${relation ? 'is-recommendation' : ''} ${selected ? 'is-selected' : ''}`}
    >
      <button
        type="button"
        className="pp-editor-result-main"
        disabled={busy || (isTrack ? !canPreview(track) || resolving : selected)}
        aria-label={`${item.title} ${isTrack ? (playing ? (en ? 'pause' : '일시정지') : en ? 'play' : '재생') : en ? 'add item' : '항목 추가'}`}
        onClick={isTrack ? play : onAdd}
      >
        <span className="pp-editor-result-artwork">
          <Artwork item={item} />
          {isTrack && (
            <span className="pp-editor-preview-shade">
              <span className="pp-editor-preview-icon">
                {active && <PlaybackRing />}
                {resolving ? (
                  <span className="pp-editor-resolving" />
                ) : playing ? (
                  <FiPause size={13} />
                ) : (
                  <FiPlay size={14} />
                )}
              </span>
            </span>
          )}
        </span>
        <Copy {...{ item, type, relation, en }} search />
      </button>
      <button
        className="pp-editor-add"
        type="button"
        disabled={busy || selected}
        aria-label={`${item.title} ${en ? 'add' : '추가'}`}
        aria-pressed={selected}
        onClick={onAdd}
      >
        {selected ? <FiCheck size={19} /> : <FiPlus size={21} />}
      </button>
      {active && playback.error && (
        <span className="pp-editor-preview-error" role="status">
          {en
            ? 'Playback unavailable. Press play to retry.'
            : '재생하지 못했어요. 재생 버튼으로 다시 시도해 주세요.'}
        </span>
      )}
    </li>
  );
}
export default function PlaylistEditorItems({
  items,
  setItems,
  results,
  type,
  busy,
  en,
  onAdd,
  recommendations = { children: {}, loading: new Set(), errors: new Set() },
  previewOwner,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const keys = new Set(items.map(editorItemKey));
  const renderBranch = (item, depth = 0, relation = null, ancestors = new Set()) => {
    const key = editorItemKey(item);
    if (ancestors.has(key)) return null;
    const chain = new Set(ancestors).add(key),
      children = recommendations.children[key] || [];
    return (
      <React.Fragment key={key}>
        <ResultRow
          {...{ item, type, relation, busy, en }}
          owner={previewOwner}
          selected={keys.has(key)}
          onAdd={() => onAdd(item, depth)}
        />
        {recommendations.loading.has(key) && !children.length && (
          <li className="pp-editor-recommendation-status" role="status">
            {en ? 'Finding recommendations…' : '추천을 찾고 있어요.'}
          </li>
        )}
        {recommendations.errors.has(key) && (
          <li className="pp-editor-recommendation-status">
            <span>{en ? 'We could not load recommendations.' : '추천을 불러오지 못했어요.'}</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => recommendations.load(item, depth, true)}
            >
              {en ? 'Retry' : '다시 시도'}
            </button>
          </li>
        )}
        {children.map((child) => renderBranch(child.item, depth + 1, child.relation, chain))}
      </React.Fragment>
    );
  };
  return (
    <>
      {results !== null && (
        <ul className="pp-editor-results">
          {results.map((item) => renderBranch(item))}
          {!results.length && <li>{en ? 'No results.' : '검색 결과가 없습니다.'}</li>}
        </ul>
      )}
      <h2>
        {type === 'TRACK'
          ? en
            ? 'Selected tracks'
            : '선택한 곡'
          : en
            ? 'Selected albums'
            : '선택한 앨범'}{' '}
        <span>{items.length}</span>
      </h2>
      <DndContext
        sensors={sensors}
        accessibility={{
          screenReaderInstructions: {
            draggable: en
              ? 'Space to pick up, arrow keys to move, Space to drop, Escape to cancel.'
              : 'Space로 선택 후 방향키로 이동하고 Space로 놓으세요. Escape는 취소합니다.',
          },
        }}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!busy) setItems((old) => movedEditorItems(old, active.id, over?.id));
        }}
      >
        <SortableContext items={items.map(editorItemKey)} strategy={verticalListSortingStrategy}>
          <ol className="pp-editor-selected">
            {items.map((item, index) => (
              <Selected
                key={editorItemKey(item)}
                {...{ item, index, type, busy, en }}
                count={items.length}
                onMove={(a, b) =>
                  setItems((old) =>
                    movedEditorItems(old, editorItemKey(old[a]), editorItemKey(old[b])),
                  )
                }
                onRemove={(key) => setItems((old) => old.filter((x) => editorItemKey(x) !== key))}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      {!items.length && (
        <p className="pp-editor-note">
          {en ? 'There are no added items.' : '추가된 항목이 없습니다.'}
        </p>
      )}
    </>
  );
}
